import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LazyImage } from '@/components/ui/LazyImage';
import { Heart, MapPin, Package, Ruler, Lock, Star, LogIn, ShieldCheck } from 'lucide-react';
import { cn, fmt2, fmtEUR, pricePerUnit } from '@/lib/utils';

const PLACEHOLDER_IMAGE = 'https://customer-assets.emergentagent.com/job_ee642069-4fc1-425d-bfbc-e67ee20a2646/artifacts/vxtmarer_ChatGPT%20Image%202.%20M%C3%A4rz%202026%2C%2012_35_03.png';

const COUNTRY_FLAGS = {
  'germany': '\u{1F1E9}\u{1F1EA}', 'deutschland': '\u{1F1E9}\u{1F1EA}',
  'austria': '\u{1F1E6}\u{1F1F9}', 'österreich': '\u{1F1E6}\u{1F1F9}',
  'switzerland': '\u{1F1E8}\u{1F1ED}', 'schweiz': '\u{1F1E8}\u{1F1ED}',
  'france': '\u{1F1EB}\u{1F1F7}', 'frankreich': '\u{1F1EB}\u{1F1F7}',
  'italy': '\u{1F1EE}\u{1F1F9}', 'italien': '\u{1F1EE}\u{1F1F9}',
  'spain': '\u{1F1EA}\u{1F1F8}', 'spanien': '\u{1F1EA}\u{1F1F8}',
  'netherlands': '\u{1F1F3}\u{1F1F1}', 'niederlande': '\u{1F1F3}\u{1F1F1}',
  'belgium': '\u{1F1E7}\u{1F1EA}', 'belgien': '\u{1F1E7}\u{1F1EA}',
  'poland': '\u{1F1F5}\u{1F1F1}', 'polen': '\u{1F1F5}\u{1F1F1}',
  'czech republic': '\u{1F1E8}\u{1F1FF}', 'tschechien': '\u{1F1E8}\u{1F1FF}',
  'romania': '\u{1F1F7}\u{1F1F4}', 'rumänien': '\u{1F1F7}\u{1F1F4}',
  'hungary': '\u{1F1ED}\u{1F1FA}', 'ungarn': '\u{1F1ED}\u{1F1FA}',
  'portugal': '\u{1F1F5}\u{1F1F9}',
  'united kingdom': '\u{1F1EC}\u{1F1E7}', 'uk': '\u{1F1EC}\u{1F1E7}',
  'usa': '\u{1F1FA}\u{1F1F8}', 'united states': '\u{1F1FA}\u{1F1F8}',
  'china': '\u{1F1E8}\u{1F1F3}',
  'sweden': '\u{1F1F8}\u{1F1EA}', 'schweden': '\u{1F1F8}\u{1F1EA}',
  'denmark': '\u{1F1E9}\u{1F1F0}', 'dänemark': '\u{1F1E9}\u{1F1F0}',
  'finland': '\u{1F1EB}\u{1F1EE}', 'finnland': '\u{1F1EB}\u{1F1EE}',
  'norway': '\u{1F1F3}\u{1F1F4}', 'norwegen': '\u{1F1F3}\u{1F1F4}',
};

function getFlag(country) {
  if (!country) return null;
  return COUNTRY_FLAGS[country.toLowerCase().trim()] || null;
}

/** Frosted glass blur overlay with CTA */
const BlurOverlay = ({ children, testId }) => (
  <div className="relative" data-testid={testId}>
    <div className="select-none pointer-events-none" aria-hidden="true">
      {children}
    </div>
    <div
      className="absolute inset-0 backdrop-blur-[8px] bg-white/40 dark:bg-slate-900/50 rounded-lg"
      style={{ WebkitBackdropFilter: 'blur(8px)' }}
    />
  </div>
);

/** Locked field label */
const LockedLabel = ({ label }) => (
  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
    <Lock className="h-3 w-3 text-slate-400" />
    {label}
  </span>
);

/** Small inline seller rating stars */
const SellerRatingBadge = ({ rating }) => {
  if (!rating) return null;
  const stars = Math.round(rating.average);
  return (
    <div className="flex items-center gap-1 text-xs" data-testid="seller-rating-badge">
      <div className="flex gap-px">
        {[1, 2, 3, 4, 5].map((s) => (
          <Star
            key={s}
            className={cn(
              "h-3 w-3",
              s <= stars ? "fill-amber-400 text-amber-400" : "text-gray-300 dark:text-gray-600"
            )}
          />
        ))}
      </div>
      <span className="text-muted-foreground">({rating.total_ratings})</span>
    </div>
  );
};

export const MaterialCard = ({ material, onAddToWatchlist, isInWatchlist, className }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const tier = material.access_tier || (user?.approved ? 3 : user ? 2 : 1);

  const firstPhoto = material.photos?.[0];
  const catDefault = material.category_default_image;
  const photo = firstPhoto?.thumbnail_url || firstPhoto?.file_url || catDefault?.thumbnail_url || catDefault?.image_url || PLACEHOLDER_IMAGE;
  const blurSrc = firstPhoto?.blur_hash || catDefault?.blur_hash || null;
  const isSpecial = material.is_special;
  const flag = getFlag(material.site_country);

  return (
    <Card
      className={cn(
        "group overflow-hidden hover:shadow-lg hover:shadow-orange-500/10 transition-all duration-300",
        className
      )}
      data-testid={`material-card-${material.material_id}`}
    >
      <div className="relative">
        <Link to={`/material/${material.material_id}`}>
          <div className="aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-800">
            <LazyImage
              src={photo}
              blurSrc={blurSrc}
              alt={material.common_title}
              className="w-full h-full group-hover:scale-105 transition-transform duration-300"
              fallbackSrc={PLACEHOLDER_IMAGE}
            />
          </div>
        </Link>

        {/* Top-right badges */}
        <div className="absolute top-2 right-2 flex flex-col gap-1">
          {isSpecial && (
            <Badge className="bg-amber-500 text-white text-xs gap-1" data-testid={`special-badge-${material.material_id}`}>
              <Star className="h-3 w-3" />
              {t('badge.special', 'Spezialmaterial')}
            </Badge>
          )}
          {tier >= 3 && material.is_internal && (
            <Badge variant="secondary" className="bg-blue-500 text-white text-xs">
              {t('badge.groupOnly', 'Nur Gruppe')}
            </Badge>
          )}
          {tier >= 3 && !material.is_internal && material.is_internal !== null && (
            <Badge variant="secondary" className="bg-green-500 text-white text-xs">
              {t('badge.external', 'Extern')}
            </Badge>
          )}
          {tier >= 3 && material.has_missing_docs && (
            <Badge variant="outline" className="bg-background text-orange-600 border-orange-300 text-xs">
              {t('badge.missingDocs', 'Docs fehlen')}
            </Badge>
          )}
        </div>

        {/* Quantity range badge (Tier 1 only) */}
        {tier === 1 && material.quantity_range && (
          <div className="absolute bottom-2 left-2">
            <Badge className="bg-slate-800/80 text-white text-xs backdrop-blur-sm" data-testid="quantity-range-badge">
              <Package className="h-3 w-3 mr-1" />
              {material.quantity_range}
            </Badge>
          </div>
        )}

        {/* Country flag badge */}
        {flag && (
          <div className="absolute bottom-2 right-2">
            <span className="text-lg drop-shadow-md" data-testid="country-flag" title={material.site_country}>
              {flag}
            </span>
          </div>
        )}

        {/* Watchlist Button */}
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "absolute top-2 left-2 h-8 w-8 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm",
            isInWatchlist && "text-red-500"
          )}
          onClick={(e) => {
            e.preventDefault();
            onAddToWatchlist?.(material.material_id);
          }}
          data-testid={`watchlist-btn-${material.material_id}`}
        >
          <Heart className={cn("h-4 w-4", isInWatchlist && "fill-current")} />
        </Button>
      </div>

      <CardContent className="p-4 space-y-3">
        {/* Title - always visible */}
        <Link to={`/material/${material.material_id}`}>
          <h3 className="font-manrope font-semibold text-sm line-clamp-2 hover:text-orange-500 transition-colors" data-testid="material-title">
            {material.common_title}
          </h3>
        </Link>

        {/* Manufacturer - always visible */}
        <p className="text-xs text-muted-foreground truncate flex items-center gap-1" data-testid="material-manufacturer">
          {material.manufacturer_verified && <ShieldCheck className="h-3 w-3 text-emerald-500 shrink-0" />}
          {material.manufacturer_name}
        </p>

        {/* Seller Rating - always visible */}
        {material.seller_rating && <SellerRatingBadge rating={material.seller_rating} />}

        {/* ====== TIER 1: Public (not logged in) ====== */}
        {tier === 1 && (
          <>
            {/* Blurred hidden fields */}
            <BlurOverlay testId="tier1-blur-overlay">
              <div className="space-y-2 p-2">
                <LockedLabel label={t('material.quantity', 'Menge')} />
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
                <LockedLabel label={t('material.price', 'Preis')} />
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
                <LockedLabel label={t('material.site', 'Standort')} />
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4" />
              </div>
            </BlurOverlay>
            <Button
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium gap-2"
              onClick={(e) => {
                e.preventDefault();
                navigate('/register');
              }}
              data-testid="cta-sign-in"
            >
              <LogIn className="h-4 w-4" />
              {t('cta.signInToSeeDetails', 'Anmelden um Details zu sehen')}
            </Button>
          </>
        )}

        {/* ====== TIER 2: Logged in, not approved ====== */}
        {tier === 2 && (
          <>
            {/* Visible: quantity, width, age bucket */}
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {material.total_quantity != null && (
                <span className="flex items-center gap-1" data-testid="material-quantity">
                  <Package className="h-3 w-3" />
                  {fmt2(material.total_quantity)} {material.uom}
                </span>
              )}
              {material.material_width_mm && (
                <span className="flex items-center gap-1" data-testid="material-width">
                  <Ruler className="h-3 w-3" />
                  {material.material_width_mm}mm
                </span>
              )}
            </div>

            {/* Blurred hidden fields: price, site, contact */}
            <BlurOverlay testId="tier2-blur-overlay">
              <div className="space-y-2 p-2">
                <LockedLabel label={t('material.price', 'Preis')} />
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-1/2" />
                <LockedLabel label={t('material.site', 'Standort')} />
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
              </div>
            </BlurOverlay>
            <Button
              variant="outline"
              className="w-full border-orange-400 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30 text-sm font-medium gap-2"
              onClick={(e) => {
                e.preventDefault();
                navigate('/search');
                // Trigger a toast asking them to request approval
                import('sonner').then(({ toast }) => {
                  toast.info(t('cta.requestApprovalHint', 'Bitte kontaktieren Sie Ihren Administrator für die Freigabe.'));
                });
              }}
              data-testid="cta-request-approval"
            >
              <ShieldCheck className="h-4 w-4" />
              {t('cta.requestApproval', 'Freigabe anfragen um Preise zu sehen')}
            </Button>
          </>
        )}

        {/* ====== TIER 3: Approved user ====== */}
        {tier === 3 && (
          <>
            {/* Specs */}
            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {material.material_width_mm && (
                <span className="flex items-center gap-1" data-testid="material-width">
                  <Ruler className="h-3 w-3" />
                  {material.material_width_mm}mm
                </span>
              )}
              {material.sku_count > 0 && (
                <span className="flex items-center gap-1" data-testid="material-sku-count">
                  <Package className="h-3 w-3" />
                  {material.sku_count} {material.sku_count === 1 ? 'Position' : 'Positionen'}
                </span>
              )}
            </div>

            {/* Location */}
            {(material.site_city || material.site_country) && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="material-location">
                <MapPin className="h-3 w-3" />
                <span className="truncate">
                  {material.site_name && `${material.site_name}, `}
                  {[material.site_city, material.site_country].filter(Boolean).join(', ')}
                </span>
              </div>
            )}

            {/* Price & Quantity */}
            <div className="flex items-center justify-between pt-2 border-t" data-testid="material-pricing">
              <div>
                <p className="text-lg font-bold text-orange-500">
                  {material.total_book_value_kEUR > 0 ? `\u20AC${fmtEUR(material.total_book_value_kEUR)}` : t('material.priceOnRequest', 'Preis auf Anfrage')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fmt2(material.total_quantity)} {material.uom}
                </p>
                {material.total_book_value_kEUR > 0 && material.total_quantity > 0 && (
                  <p className="text-xs text-orange-400 font-medium">
                    {fmt2(pricePerUnit(material.total_book_value_kEUR * 1000, material.total_quantity))} EUR/{material.uom}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
